import { useQueryClient } from '@tanstack/react-query';
import { AdminFormModal, ModalStage } from '../../../AdminFormModal';
import { AdminTeams } from '@platform/client';
import { useContext, useEffect, useState } from 'react';
import { AdminTableContext } from '../../../AdminTable';
import { AddMemberInputRow, RemovePendingNewMemberButton } from './ModalFormButtons';
import { NewMember } from '@platform/client/schemas';

export const CreateTeamModal = ({
  cohortId,
  onClose,
  incomingMembers, // allow preselecting members from free agents
}: {
  cohortId: number;
  onClose: () => void;
  incomingMembers?: NewMember[];
}) => {
  const [members, setMembers] = useState(incomingMembers);
  const [inputMemberId, setInputMemberId] = useState<number>();
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { data } = AdminTeams.useGetNewMember(
    {
      user_id: inputMemberId ?? 0,
    },
    { query: { enabled: !!inputMemberId } }, // maybe enable only if not already in list?
  );
  useEffect(() => {
    if (data && inputMemberId) {
      setMembers((prev) => {
        if (prev?.find((prevMember) => prevMember === data.member)) {
          return prev;
        }
        if (prev) {
          return [...prev, data.member];
        } else {
          return [data.member];
        }
      });
    }
  }, [setMembers, data, inputMemberId]);
  const closeModal = () => {
    setMembers(undefined);
    onClose();
  };
  const queryClient = useQueryClient();
  const createTeamMutator = AdminTeams.useCreateTeam({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetTeamsQueryKey({
            cohort_id: cohortId,
            page: adminQueryOptionsV2.page,
            limit: adminQueryOptionsV2.limit,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetFreeAgentsQueryKey({
            cohort_id: cohortId,
            page: adminQueryOptionsV2.page,
            limit: adminQueryOptionsV2.limit,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetTeamsCountsQueryKey({
            cohort_id: cohortId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetTeamIdsQueryKey({ cohort_id: cohortId }),
        });
        setModalStage('success');
      },
      onError: () => {
        setModalStage('error');
      },
    },
  });
  return (
    <AdminFormModal
      title='Create new team'
      modalStage={modalStage}
      onClose={closeModal}
      submitCTA={{
        text: 'Create team',
        callback: () => {
          if (members && members.length > 0) {
            createTeamMutator.mutate({
              data: { memberIds: members.map((member) => member.id), cohortId },
            });
          }
        },
        isDisabled: () => !members || members.length === 0,
      }}
    >
      {members?.map((member) => (
        <div key={`new-${member.id}`} className='flex justify-between text-success-500'>
          {member.name} (ID: {member.id})
          <RemovePendingNewMemberButton pendingMemberId={member.id} setNewMembers={setMembers} />
        </div>
      ))}
      <AddMemberInputRow setAddNewMemberId={setInputMemberId} />
    </AdminFormModal>
  );
};
